import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { createPasswordSchema } from '@/utils/schemas'

import { passwordChangedSchema, useSensitiveRequest } from './securityClient'
import type { FactorMethod, SecurityStatus } from './securityClient'
import { FactorFields, SecretField } from './SecurityFields'

export function PasswordChangeForm({
  status,
  onSuccess,
  onError,
}: {
  status: SecurityStatus
  onSuccess: () => void
  onError: (error: unknown, mutation?: boolean) => void
}) {
  const { t } = useTranslation()
  const request = useSensitiveRequest()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [method, setMethod] = useState<FactorMethod>('totp')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const lock = useRef(false)
  return (
    <section className="flex flex-col gap-4 border-b border-neutralColor5 pb-6">
      <h2 className="text-xl font-medium">{t('security.passwordTitle')}</h2>
      {(status.passwordManagement === 'microsoft' ||
        status.passwordManagement === 'mixed') && (
        <p className="text-sm text-neutralColor60">
          {t('security.microsoftPassword')}
        </p>
      )}
      {status.passwordManagement === 'unknown' && (
        <p className="text-sm text-neutralColor60">
          {t('security.unknownPassword')}
        </p>
      )}
      {status.passwordManagement === 'wecheck' && (
        <p className="text-sm text-neutralColor60">
          {t('security.localPassword')}
        </p>
      )}
      {status.canChangePassword &&
        status.passwordManagement !== 'microsoft' && (
          <form
            className="flex max-w-lg flex-col gap-4"
            onSubmit={async (event) => {
              event.preventDefault()
              if (lock.current) return
              if (newPassword !== confirmPassword) {
                setError('security.passwordMismatch')
                return
              }
              const validation = createPasswordSchema.safeParse({
                createPassword: newPassword,
                confirmPassword,
              })
              if (!validation.success) {
                setError(validation.error.issues[0].message)
                return
              }
              lock.current = true
              setBusy(true)
              setError('')
              try {
                await request(
                  '/api/v1/auth/security/password',
                  passwordChangedSchema,
                  {
                    authenticated: true,
                    body: {
                      currentPassword,
                      newPassword,
                      ...(status.mfaEnabled
                        ? { method, code: code.trim() }
                        : {}),
                    },
                  },
                )
                setCurrentPassword('')
                setNewPassword('')
                setConfirmPassword('')
                setCode('')
                onSuccess()
              } catch (e) {
                setCurrentPassword('')
                setCode('')
                onError(e, true)
              } finally {
                lock.current = false
                setBusy(false)
              }
            }}
          >
            <SecretField
              label={t('security.currentPassword')}
              value={currentPassword}
              required
              disabled={busy}
              autoComplete="current-password"
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <SecretField
              label={t('security.newPassword')}
              value={newPassword}
              required
              disabled={busy}
              autoComplete="new-password"
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              maxLength={128}
            />
            <SecretField
              label={t('security.confirmPassword')}
              value={confirmPassword}
              required
              disabled={busy}
              autoComplete="new-password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={8}
              maxLength={128}
            />
            {status.mfaEnabled && (
              <FactorFields
                method={method}
                code={code}
                onCode={setCode}
                disabled={busy}
                onMethod={(next) => {
                  setMethod(next)
                  setCode('')
                }}
              />
            )}
            {error && (
              <p role="alert" className="text-sm text-dynamicRed">
                {t(error)}
              </p>
            )}
            <p className="text-sm text-neutralColor60">
              {t('security.passwordSignOut')}
            </p>
            <Button
              type="submit"
              isLoading={busy}
              disabled={busy}
              className="self-start"
            >
              {t('security.changePassword')}
            </Button>
          </form>
        )}
    </section>
  )
}
